import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Layout } from '../components/layout/Layout'
import { AlbumView } from '../components/album/AlbumView'
import { VisibilityBadge } from '../components/common/VisibilityBadge'
import { publicApiClient, getShareToken, setShareToken } from '../api/client'
import type { Album as AlbumType } from '../types/album'
import { ArrowLeft, Lock, Loader2 } from 'lucide-react'

type AlbumResponse = AlbumType & { locked?: boolean; requiresShareToken?: boolean }

export const Album = () => {
  const { id } = useParams<{ id: string }>()
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)
  const [token, setToken] = useState<string | null>(() => (id ? getShareToken(id) : null))

  const { data: album, isLoading } = useQuery({
    queryKey: ['album', id, token],
    queryFn: async () => {
      const headers: Record<string, string> = {}
      if (token) headers['X-Share-Token'] = token
      const response = await publicApiClient.get(`/albums/${id}`, { headers })
      return response.data.data as AlbumResponse
    },
    enabled: !!id,
    retry: false,
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id || !password) return

    setIsVerifying(true)
    setError('')

    try {
      const response = await publicApiClient.post(`/share/${id}/verify`, { password })
      const accessToken = response.data.data.accessToken
      setShareToken(id, accessToken)
      setToken(accessToken)
    } catch {
      setError('Incorrect password')
    } finally {
      setIsVerifying(false)
    }
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-4 bg-gray-200 rounded w-1/2" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-square bg-gray-200 rounded-lg" />
            ))}
          </div>
        </div>
      </Layout>
    )
  }

  if (!album) {
    return (
      <Layout>
        <div className="text-center py-20">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Album not found</h1>
          <p className="text-gray-500 mb-6">This album doesn't exist or has been deleted</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Gallery
          </Link>
        </div>
      </Layout>
    )
  }

  // Locked-response (200 + locked: true, metadata-only): album is discoverable
  // or a misconfigured private-with-password album. Render an inline password
  // prompt so the visitor can unlock without leaving the page.
  if (album.locked) {
    return (
      <Layout title={album.name}>
        <div className="max-w-md mx-auto py-20">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{album.name}</h1>
            {album.description && (
              <p className="text-gray-500 mb-2">{album.description}</p>
            )}
            <p className="text-sm text-gray-500">
              This album is locked. Enter the share password to view the contents.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              autoFocus
            />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={isVerifying || !password}
              className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isVerifying ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'View Album'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Gallery
            </Link>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout
      title={album.name}
      actions={
        <div className="flex items-center gap-2">
          {album.visibility !== 'public' && (
            <VisibilityBadge visibility={album.visibility} />
          )}
          <Link
            to="/"
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
        </div>
      }
    >
      <AlbumView album={album} albumId={id!} shareToken={token ?? undefined} />
    </Layout>
  )
}